import React from 'react';
import { Copy } from 'lucide-react';
import './OxiTopologyDiagram.css';

// Network path for an OXI source, drawn from the routes that actually exist rather than from a sketch:
//
//   * hconnect side — `/oxi/{account}` on the connectors API (terraform/connectors-apigateway.tf) accepts
//     BOTH GET and POST, served by connectors-oxi-dequeue and connectors-oxi-enqueue respectively.
//     `{account}` is this task's API key.
//
//   * SyncConnect side — the client's HTTP listener (syncconnect/Program.cs) binds localhost, 127.0.0.1
//     and `+` (all interfaces) on the configured port, serves `GET /health` in every mode and
//     `GET|POST /oxi/{apiKey}` in HILTON mode, then forwards to `{hconnect url}/oxi/{apiKey}`.
//
// The point the diagram has to make: **every connection is outbound from the property**. OXI is the HTTP
// client in both directions — POST delivers messages to us, GET collects the ones we are holding for it —
// so no inbound firewall rule is needed at the hotel. And the API key and path are identical with or
// without SyncConnect, which is what makes SyncConnect a drop-in relay.

const DEFAULT_LISTENER_PORT = '8081';

const Node = ({ title, subtitle, host, port, kind }) => (
    <div className={`oxi-node oxi-node-${kind}`}>
        <div className="oxi-node-title">{title}</div>
        {subtitle && <div className="oxi-node-sub">{subtitle}</div>}
        {host && (
            <div className="oxi-node-host">
                {host}{port && <span className="oxi-node-port">:{port}</span>}
            </div>
        )}
    </div>
);

// A bare double-headed connector between two nodes. Traffic flows both ways, so a single arrowhead would
// contradict the Hub-to-OXI direction. It carries no text: full https URLs do not fit between two boxes
// and previously overflowed onto them — they are listed at full width underneath instead.
const Connector = () => (
    <div className="oxi-arrow" aria-hidden="true">
        <span className="oxi-arrow-head-left" />
        <span className="oxi-arrow-shaft" />
        <span className="oxi-arrow-head" />
    </div>
);

// One direction of one leg: the direction the DATA travels (not the HTTP verb), the full URL for that leg
// so it can be pasted straight into OXI's configuration, what it carries, and a copy button.
// A grid rather than a flex row so the direction and note columns line up across every row.
const DirectionRow = ({ l, onCopy }) => (
    <div className="oxi-dir-row">
        <span className="oxi-link-dir">{l.reverse ? '←' : '→'} {l.direction}</span>
        <span className="oxi-link-url">{l.url}</span>
        <span className="oxi-link-note">{l.note}</span>
        {/* Same class and icon size as every other copy button on this screen (the API Key field and the
            verification block) — this is the page's existing button, not a variant of it. */}
        <button
            type="button"
            className="btn-secondary"
            title={`Copy the ${l.direction} URL`}
            onClick={() => onCopy?.(l.url, `${l.direction} URL copied to clipboard`)}
        >
            <Copy size={16} />
        </button>
    </div>
);

// A leg of the path — its two directions, captioned with the hop when there is more than one leg.
const Leg = ({ caption, lines, onCopy }) => (
    <div className="oxi-leg">
        {caption && <div className="oxi-leg-caption">{caption}</div>}
        {lines.map((l, i) => <DirectionRow key={i} l={l} onCopy={onCopy} />)}
    </div>
);

const OxiTopologyDiagram = ({ syncHost, apiKey, usesSyncConnect, listenerPort, onCopy }) => {
    const key  = apiKey || '<api key>';
    const host = (syncHost || '').replace(/^https?:\/\//, '');
    const port = listenerPort || DEFAULT_LISTENER_PORT;
    const viaConnect = usesSyncConnect === 'Y';

    const hubUrl = `https://${host || '<sync host>'}/oxi/${key}`;
    // SyncConnect runs on the OPERA/OXI machine itself, so OXI reaches it on the loopback address.
    const connectUrl = `http://localhost:${port}/oxi/${key}`;

    // Direction labels name the two ENDS OF THAT LEG, not the ends of the whole path — on the first hop
    // of a SyncConnect setup the traffic is OXI ↔ SyncConnect, and calling it "OXI-to-Hub" would point at
    // the wrong box.
    const legLines = (url, from, to) => [
        { direction: `${from}-to-${to}`, url, note: 'delivers messages' },
        { direction: `${to}-to-${from}`, url, note: 'collects queued messages', reverse: true },
    ];

    return (
        <div className="oxi-diagram">
            <div className="oxi-diagram-head">
                <span className="oxi-diagram-title">Connection path</span>
                {/* ↔ — traffic flows both ways on every hop. */}
                <span className={`oxi-diagram-mode ${viaConnect ? 'oxi-mode-connect' : 'oxi-mode-direct'}`}>
                    {viaConnect ? 'OXI ↔ SyncConnect ↔ Hub' : 'OXI ↔ Hub (direct)'}
                </span>
            </div>

            {/* Topology only — who talks to whom. The URLs live underneath, where they have the width. */}
            <div className="oxi-flow">
                <Node
                    kind="property"
                    title="OPERA / OXI"
                    subtitle="at the property"
                />
                <Connector />
                {viaConnect && (
                    <>
                        <Node
                            kind="connect"
                            title="SyncConnect"
                            subtitle="Windows service, at the property"
                            host="localhost"
                            port={port}
                        />
                        <Connector />
                    </>
                )}
                <Node
                    kind="hub"
                    title="hconnect Hub"
                    host={host || '<sync host>'}
                    port="443"
                />
            </div>

            <div className="oxi-legs">
                {viaConnect ? (
                    <>
                        <Leg caption="OPERA / OXI ↔ SyncConnect"
                             lines={legLines(connectUrl, 'OXI', 'SyncConnect')} onCopy={onCopy} />
                        <Leg caption="SyncConnect ↔ hconnect Hub"
                             lines={legLines(hubUrl, 'SyncConnect', 'Hub')} onCopy={onCopy} />
                    </>
                ) : (
                    <Leg lines={legLines(hubUrl, 'OXI', 'Hub')} onCopy={onCopy} />
                )}
            </div>

        </div>
    );
};

export default OxiTopologyDiagram;
